import { memo, useCallback } from "react";

import {
  ShopifyProvider,
  CartProvider as HydrogenCartProvider,
} from "@shopify/hydrogen-react";
import { ErrorBoundary } from "react-error-boundary";
import { SWRConfig } from "swr";

import { CartActiveProductProvider } from "@cart/CartActiveProductContext";
import { CartDrawerManager } from "@cart/CartDrawer";
import { CartGiftWithPurchaseProvider } from "@cart/CartGiftWithPurchaseContext";
import { LegacyCartProvider } from "@cart/LegacyCartContext";
import { LoadingOverlay } from "@components/LoadingOverlay";
import CartFragment from "@graphql/cart.fragment.graphql";
import {
  STOREFRONT_ACCESS_TOKEN,
  STOREFRONT_API_VERSION,
  SWR_SETTINGS,
} from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";

const CartProviderBase = () => {
  const emitAddCartLine = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.ADD_CART_LINE, {
      ui: {
        cartLineAdding: true,
      },
    });
  }, []);

  const emitUpdateCartLine = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.UPDATE_CART_LINE, {
      ui: {
        cartLineUpdating: true,
      },
    });
  }, []);

  const emitRemoveCartLine = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.REMOVE_CART_LINE, {
      ui: {
        cartLineRemoving: true,
      },
    });
  }, []);

  const emitAddCartLineComplete = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.ADD_CART_LINE_COMPLETE, {
      ui: {
        cartLineAdding: false,
      },
    });
  }, []);

  const emitUpdateCartLineComplete = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.UPDATE_CART_LINE_COMPLETE, {
      ui: {
        cartLineUpdating: false,
      },
    });
  }, []);

  const emitRemoveCartLineComplete = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.REMOVE_CART_LINE_COMPLETE, {
      ui: {
        cartLineRemoving: false,
      },
    });
  }, []);

  const emitUpdateGiftNoteComplete = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.UPDATE_GIFT_NOTE_COMPLETE, {
      ui: {
        giftNoteDrawerOpen: false,
      },
    });
  }, []);

  return (
    <ErrorBoundary fallback={<LoadingOverlay show />}>
      <ShopifyProvider
        countryIsoCode={window.Shopify.country}
        languageIsoCode="EN"
        storeDomain={window.Shopify.shop}
        storefrontApiVersion={STOREFRONT_API_VERSION}
        storefrontToken={STOREFRONT_ACCESS_TOKEN}
      >
        <HydrogenCartProvider
          cartFragment={String(CartFragment)}
          onLineAdd={emitAddCartLine}
          onLineAddComplete={emitAddCartLineComplete}
          onLineRemove={emitRemoveCartLine}
          onLineRemoveComplete={emitRemoveCartLineComplete}
          onLineUpdate={emitUpdateCartLine}
          onLineUpdateComplete={emitUpdateCartLineComplete}
          onNoteUpdateComplete={emitUpdateGiftNoteComplete}
        >
          <SWRConfig value={SWR_SETTINGS}>
            <LegacyCartProvider>
              <CartActiveProductProvider>
                <CartGiftWithPurchaseProvider>
                  <CartDrawerManager />
                </CartGiftWithPurchaseProvider>
              </CartActiveProductProvider>
            </LegacyCartProvider>
          </SWRConfig>
        </HydrogenCartProvider>
      </ShopifyProvider>
    </ErrorBoundary>
  );
};

export const CartProvider = memo(CartProviderBase);
